/**
 * Focus Area Utilities
 * Shared constants and logic for focus areas across onboarding, settings, and workout views.
 */

export const FOCUS_AREA_OPTIONS = [
  { id: 'arms_shoulders', label: 'Arms' },
  { id: 'chest_push', label: 'Chest' },
  { id: 'back_posture', label: 'Back' },
  { id: 'legs_thighs', label: 'Legs' },
  { id: 'glutes_booty', label: 'Glutes' },
  { id: 'core_abs', label: 'Core' },
];

// Maps focus area IDs to muscle keywords that appear in workout day.type field
const FOCUS_AREA_TO_DAY_MUSCLES = {
  glutes_booty: ['glutes', 'hamstrings', 'booty'],
  legs_thighs: ['quads', 'hamstrings', 'calves', 'legs', 'quadriceps'],
  arms_shoulders: ['biceps', 'triceps', 'shoulders', 'arms', 'deltoids'],
  back_posture: ['back', 'lats', 'upper back', 'traps', 'rear delts'],
  chest_push: ['chest', 'pecs'],
  core_abs: ['core', 'abs', 'obliques'],
};

/**
 * Determine if a workout day matches the user's focus areas.
 * Returns { matches: boolean, matchedAreas: string[] }
 */
export function getDayFocusMatch(day, userFocusAreas, focusAreaMode) {
  if (!userFocusAreas?.length || focusAreaMode === 'full_body' || focusAreaMode === 'skipped') {
    return { matches: false, matchedAreas: [] };
  }

  // PRIMARY: Use backend-derived focus_areas if present (set by _derive_day_focus_areas)
  if (day.focus_areas?.length) {
    const backendMatched = day.focus_areas.filter(a => userFocusAreas.includes(a));
    if (backendMatched.length > 0) {
      return { matches: true, matchedAreas: backendMatched };
    }
  }

  // FALLBACK: Text-based matching for plans without backend focus_areas
  const dayType = (day.type || '').toLowerCase();
  const dayName = (day.day_name || '').toLowerCase();
  const combined = `${dayType} ${dayName}`;
  const matchedAreas = [];

  // For full_body or generic sessions, inspect actual exercise muscles
  const isGeneric = (day.session_type || '').toLowerCase() === 'full_body'
    || dayType.includes('full body')
    || dayType === 'upper body'
    || dayType === 'lower body';

  let exerciseMuscles = null;
  if (isGeneric && day.main?.length) {
    exerciseMuscles = new Set();
    for (const ex of day.main) {
      const pm = (ex.primary_muscle || '').toLowerCase();
      if (pm) exerciseMuscles.add(pm);
    }
  }

  for (const areaId of userFocusAreas) {
    const muscles = FOCUS_AREA_TO_DAY_MUSCLES[areaId] || [];
    // Match via day type string (works for narrow splits: "Chest & Triceps", "Back & Biceps")
    const typeMatch = muscles.some(m => combined.includes(m));
    // For generic/full_body days, also check actual exercise muscles
    const deepMatch = exerciseMuscles ? muscles.some(m => exerciseMuscles.has(m)) : false;
    if (typeMatch || deepMatch) {
      matchedAreas.push(areaId);
    }
  }

  return { matches: matchedAreas.length > 0, matchedAreas };
}

/**
 * Determine if day highlighting should be suppressed.
 * 
 * For NARROW splits (push/pull/legs, upper/lower), suppress when >75% match
 * because highlighting every day is noise.
 * 
 * For FULL-BODY splits, NEVER suppress — the user chose specific focus areas
 * and wants to see which days emphasize them, even if all days technically
 * contain those muscle groups (the match shows emphasis, not exclusivity).
 */
export function shouldSuppressHighlighting(days, userFocusAreas, focusAreaMode) {
  if (!userFocusAreas?.length || focusAreaMode === 'full_body' || focusAreaMode === 'skipped') {
    return true;
  }

  const trainingDays = (days || []).filter(d => {
    const t = (d.type || '').toLowerCase();
    return t !== 'recovery' && t !== 'rest';
  });
  if (trainingDays.length === 0) return true;

  // Detect full-body split: if ALL training days contain "full body" in type or session_type
  const isFullBodySplit = trainingDays.every(d => {
    const dtype = (d.type || '').toLowerCase();
    const stype = (d.session_type || '').toLowerCase();
    const dname = (d.day_name || '').toLowerCase();
    return dtype.includes('full body') || stype === 'full_body' || dname.includes('full body');
  });

  // Full-body splits: never suppress — user wants to see focus emphasis per day
  if (isFullBodySplit) return false;

  const matchingCount = trainingDays.filter(d => getDayFocusMatch(d, userFocusAreas, focusAreaMode).matches).length;
  return matchingCount / trainingDays.length > 0.75;
}

/**
 * Get a human-readable label for matched focus areas on a day.
 */
export function getFocusLabel(matchedAreas) {
  if (!matchedAreas?.length) return '';
  const labels = matchedAreas.map(id => {
    const opt = FOCUS_AREA_OPTIONS.find(o => o.id === id);
    return opt ? opt.label : id;
  });
  return labels.length === 1 ? `${labels[0]} FOCUS` : `${labels.join(' + ')} FOCUS`;
}
